import React, { useEffect, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { formatINR } from '@/lib/format';
import { PromoStatus } from '@/components/admin/PromotionsAdmin';
import { ClickReportDialog } from '@/components/banners/ClickReportDialog';
import { platformLabel, promoLiveLine } from '@/components/banners/PlatformPicker';
import { PromoRequestForm } from '@/components/promote/PromoRequestForm';
import { payPromotion } from '@/components/promote/payPromotion';
import { useAuth } from '@/lib/auth';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Megaphone, BarChart3, CreditCard, ImageOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const StatusNote = ({ p }) => {
    const n = {
        requested: ['text-amber-700', `Request sent for ${p.start_date} — waiting for admin approval. We will WhatsApp you.`],
        request_rejected: ['text-rose-700', `Request declined. Reason: ${p.reject_reason || '—'}`],
        request_approved: ['text-emerald-700', `Approved! Pay now to confirm your slot from ${p.start_date}.`],
        pending_approval: ['text-amber-700', `Paid — final review in progress. Requested start ${p.start_date}.`],
        rejected: ['text-rose-700', `Not approved: ${p.reject_reason || '—'} · ${p.refund_status === 'refunded' ? 'Refund issued (5–7 working days)' : 'Refund being processed'}`],
    }[p.status];
    if (p.status === 'approved') return <div className="text-xs text-emerald-700">{promoLiveLine(p)}</div>;
    return n ? <div className={`text-xs ${n[0]}`}>{n[1]}</div> : null;
};

const PayNow = ({ p, gateways, onPaid }) => {
    const { user } = useAuth();
    const [gw, setGw] = useState(gateways[0]?.key || 'razorpay');
    const [busy, setBusy] = useState(false);
    if (!gateways.length) return <span className="text-xs text-muted-foreground">Online payment unavailable right now</span>;
    return (
        <div className="flex items-center gap-2">
            {gateways.length > 1 && <Select value={gw} onValueChange={setGw}><SelectTrigger className="h-8 w-36" data-testid={`my-promo-gateway-${p.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{gateways.map(g => <SelectItem key={g.key} value={g.key}>{g.name}</SelectItem>)}</SelectContent></Select>}
            <Button size="sm" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`my-promo-pay-${p.id}`}
                onClick={() => { setBusy(true); payPromotion(p, gw, user, () => { setBusy(false); onPaid(); }); }}>
                <CreditCard className="h-4 w-4 mr-1" /> {busy ? 'Opening…' : `Pay ${formatINR(p.amount_inr)}`}
            </Button>
        </div>
    );
};

const PromoItem = ({ p, gateways, onPaid, onReport }) => (
    <div className="grid gap-3 sm:grid-cols-[180px_1fr_auto] items-center rounded-lg border border-border p-3" data-testid={`my-promo-${p.id}`}>
        {p.image_url ? <img src={`${API}${p.image_url}`} alt={p.host_name || 'Banner'} className="aspect-[12/5] w-full rounded-md object-cover" />
            : <div className="aspect-[12/5] w-full rounded-md bg-secondary flex items-center justify-center text-muted-foreground"><ImageOff className="h-5 w-5" /></div>}
        <div className="text-sm">
            <div className="flex items-center gap-2 flex-wrap"><span className="font-medium">{p.host_name || p.package_name || 'Banner promotion'}{p.in_app_id ? ` · ID ${p.in_app_id}` : ''}</span><PromoStatus p={p} /></div>
            <div className="text-xs text-muted-foreground">{p.platform ? `${platformLabel(p.platform)} · ` : ''}{p.days} days · {formatINR(p.amount_inr)}</div>
            <StatusNote p={p} />
        </div>
        {p.status === 'request_approved' && <PayNow p={p} gateways={gateways} onPaid={onPaid} />}
        {p.status === 'approved' && p.start_at && <Button size="sm" variant="outline" onClick={() => onReport(p)} data-testid={`my-promo-report-${p.id}`}><BarChart3 className="h-4 w-4 mr-1" /> Clicks report</Button>}
    </div>
);

export default function PromotePage() {
    const [cfg, setCfg] = useState(null);
    const [mine, setMine] = useState([]);
    const [report, setReport] = useState(null);
    const loadMine = () => api.get('/promotions/mine').then(r => setMine(r.data.promotions || [])).catch(() => {});
    useEffect(() => { api.get('/promotions/config').then(r => setCfg(r.data)).catch(() => {}); loadMine(); }, []);

    return (
        <AppShell>
            <div className="max-w-4xl mx-auto space-y-4" data-testid="promote-page">
                <div>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><Megaphone className="h-3.5 w-3.5" /> Promote a banner</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold mt-1">Feature your banner on our homepage</h1>
                    <p className="text-sm text-muted-foreground mt-1">Promote a host, your agency, an event or anything else. Send your banner, dates and details → once approved, pay here → we do a final check and make it live.</p>
                </div>
                {cfg && !cfg.enabled && <Card className="p-6 text-sm text-muted-foreground" data-testid="promote-closed">Promotions are not open right now. Please check back later.</Card>}
                {cfg?.enabled && <PromoRequestForm cfg={cfg} onSent={loadMine} />}
                <Card className="p-4 sm:p-5" data-testid="my-promotions">
                    <div className="font-display font-semibold">My promotions</div>
                    <div className="mt-3 space-y-2">
                        {mine.map(p => <PromoItem key={p.id} p={p} gateways={cfg?.gateways || []} onPaid={loadMine} onReport={setReport} />)}
                        {mine.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">No promotions yet.</div>}
                    </div>
                </Card>
                <ClickReportDialog endpoint={report ? `/promotions/${report.id}/report` : null} title={report?.host_name || report?.in_app_id || 'Banner'} onClose={() => setReport(null)} />
            </div>
        </AppShell>
    );
}
